import React, { useEffect, useState } from 'react';
import { DatasetStatistic, Dataset } from '../../../types/dataset';
import { Line } from 'react-chartjs-2';
import _ from 'lodash';

type Props = {
  selectedDataset: null | Dataset;
  selectedDatasetStatistics: null | DatasetStatistic;
  statistics: DatasetStatistic[];
};

const colors = [
  { backgroundColor: 'rgba(54, 162, 235, 0.4)', borderColor: 'rgba(54, 162, 235, 1)' },
  { backgroundColor: 'rgba(219, 57, 57, 0.4)', borderColor: 'rgba(219, 57, 57, 1)' },
  { backgroundColor: 'rgba(128, 222, 75, 0.4)', borderColor: 'rgba(54, 222, 75, 1)' },
  { backgroundColor: 'rgba(202, 98, 188, 0.4)', borderColor: 'rgba(202, 98, 188, 1)' },
];

export const ChartVersionProgress = ({ statistics, selectedDataset, selectedDatasetStatistics }: Props) => {
  const [chartLabels, setChartLabels] = useState<string[]>([]);
  const [chartValues, setChartValues] = useState<any[]>([]);
  const [chartDatasets, setChartDatasets] = useState<any[]>([]);
  const [lowestValue, setLowestValue] = useState<number>(15);

  function sortVersions(version1: string, version2: string): number {
    const splitVersion1 = version1.split('.').map(Number);
    const splitVersion2 = version2.split('.').map(Number);
    const maxLength = Math.max(splitVersion1.length, splitVersion2.length);

    for (let i = 0; i < maxLength; i++) {
      const num1 = splitVersion1[i] || 0;
      const num2 = splitVersion2[i] || 0;

      if (num1 < num2) {
        return -1;
      } else if (num1 > num2) {
        return 1;
      }
    }

    return 0;
  }

  //Labels
  useEffect(() => {
    if (!selectedDataset) {
      setChartLabels([]);
      const copyStatistics = [...statistics];
      copyStatistics.forEach(datasetStatistic => {
        const labels: string[] = [];
        datasetStatistic.stats.forEach((stat: any) => {
          if (!labels.includes(stat.algorithmVersion.version)) labels.push(stat.algorithmVersion.version);
        });
        const sortedLabels = labels.sort(sortVersions);
        setChartLabels(sortedLabels);
      });
    }
    if (selectedDataset && selectedDatasetStatistics) {
      setChartLabels([]);
      const copySelectedDatasetStatistics = _.cloneDeep(selectedDatasetStatistics);
      const indexes = copySelectedDatasetStatistics.stats
        .sort((a, b) => (a.algorithmVersion.timeCreated > b.algorithmVersion.timeCreated ? 1 : -1))
        .map(stat => stat.algorithmVersion!.version);
      setChartLabels(indexes);
    }
  }, [selectedDataset, selectedDatasetStatistics, statistics]);

  //Values
  useEffect(() => {
    setChartValues([]);
    if (!selectedDataset && statistics.length) {
      chartLabels.forEach(version => {
        setChartValues(prev => [
          ...prev,
          {
            mean_label_accuracy: calculateAverage(statistics, 'mean_label_accuracy', version),
            perfect: calculateAverage(statistics, 'perfect', version),
          },
        ]);
      });
    }
    if (selectedDataset) {
      if (!selectedDatasetStatistics) {
        setChartValues([]);
      } else {
        const copySelectedDatasetStatistics: DatasetStatistic = _.cloneDeep(selectedDatasetStatistics);
        setChartValues(
          copySelectedDatasetStatistics.stats
            .sort((a, b) => (a.algorithmVersion.timeCreated > b.algorithmVersion.timeCreated ? 1 : -1))
            .map(stat => ({
              mean_label_accuracy: stat.summary.mean_label_accuracy * 100,
              perfect: stat.summary.perfect * 100,
            }))
        );
      }
    }
  }, [statistics, chartLabels, selectedDataset, selectedDatasetStatistics]);

  const calculateAverage = (array: DatasetStatistic[], type: any, version: string) => {
    const filterOnVersions = array.map(stat => stat.stats.find(item => item.algorithmVersion.version === version));
    const forAvg = filterOnVersions.reduce((total: number, next: any) => total + next?.summary[type], 0);
    const avg = ((forAvg * 100) / filterOnVersions.length).toFixed(0);
    return avg;
  };

  //Chart Datasets
  useEffect(() => {
    setChartDatasets([]);
    if (chartValues.length) {
      const allValues: number[] = ([] as number[]).concat.apply(
        [],
        chartValues.map((obj: any) => Object.values(obj))
      );
      setLowestValue(Math.min(...allValues));
      Object.keys(chartValues[0]).forEach((key: any, index: number) => {
        let dataset = {
          label: key,
          data: chartValues.map(barValue => barValue[key]),
          fill: false,
          backgroundColor: colors[index].backgroundColor,
          borderColor: colors[index].borderColor,
        };
        setChartDatasets(prev => [...prev, dataset]);
      });
    }
  }, [chartValues]);

  const data = {
    labels: chartLabels,
    datasets: chartDatasets,
  };

  const options = {
    indexAxis: 'x',
    maintainAspectRatio: false,
    scales: {
      y: {
        suggestedMin: lowestValue - 15,
        suggestedMax: 100,
      },
    },
    responsive: true,
    plugins: {
      title: {
        display: true,
        text: 'Version Progress',
        font: { size: '16px' },
      },
      legend: {
        labels: {
          boxHeight: 0,
        },
      },
    },
  };

  return (
    <>
      {(selectedDatasetStatistics && selectedDatasetStatistics.stats.length === 1) ||
      statistics[0].stats.length === 1 ? (
        <p className="container text-base m-2 text-center">
          There should be at least two versions to see any version progress.
        </p>
      ) : (
        <Line data={data} options={options} style={{ height: 450 }} />
      )}
    </>
  );
};
